import React, { FC } from 'react';
import { Typography, Unstable_Grid2 as Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { Capability, Loaders } from 'components';
import {
  useGroupData,
  useGroupModerators,
  useGroupSources,
  useWorkvivoWebhook,
} from 'features/groups/hooks';

import GroupDetails from './GroupDetails';
import GroupModerators from './GroupModerators';
import GroupSettings from './GroupSettings';
import GroupSources from './GroupSources';
import CreateActions from './CreateActions';
import EditActions from './EditActions';
import Integrations from './Integrations/Integrations';

interface IProps {
  action: 'edit' | 'create';
  isWorking: boolean;
}

const GroupForm: FC<IProps> = ({ action, isWorking }) => {
  const { t } = useTranslation();
  const { isLoading: isGroupDataLoading } = useGroupData();
  const { isLoading: isGroupModeratorsLoading } = useGroupModerators();
  const { isLoading: isGroupSourcesLoading } = useGroupSources();
  const { webhooks, workvivo_workspaces } = useWorkvivoWebhook();

  if (isGroupDataLoading || isGroupModeratorsLoading || isGroupSourcesLoading) {
    return <Loaders.Standard />;
  }

  return (
    <Grid container spacing={2}>
      <Grid xs={12}>
        <Typography color="inherit" variant="h4">
          {action === 'edit' ? t('groups:editGroup') : t('groups:createGroup')}
        </Typography>
      </Grid>

      <Grid xs={12}>
        <GroupDetails />
      </Grid>

      <Grid xs={12}>
        <GroupSettings />
      </Grid>

      {action === 'edit' && !!webhooks.length && !!workvivo_workspaces.length && (
        <Grid xs={12}>
          <Integrations />
        </Grid>
      )}

      {action === 'edit' && (
        <Capability name="Group.Function.Moderators">
          <Grid xs={12}>
            <GroupModerators />
          </Grid>
        </Capability>
      )}

      {action === 'edit' && (
        <Capability name="Group.Function.AutomatedContentSources">
          <Grid xs={12}>
            <GroupSources />
          </Grid>
        </Capability>
      )}

      <Grid xs={12}>
        {action === 'edit' ? (
          <EditActions isWorking={isWorking} />
        ) : (
          <CreateActions isWorking={isWorking} />
        )}
      </Grid>
    </Grid>
  );
};

export default GroupForm;
